import * as React from 'react';
import { useSelector } from 'react-redux';
import { useFormikContext } from 'formik';
import styled from 'styled-components';
import { Typography } from '@rtkwlf/ui';
import {
  getFullName,
  memoizedOnboardingSelectors,
  onboardingSelector,
} from '../../../../../reducers/onboarding';
import {
  Checkbox,
  RadioButtons,
} from '../../../../../widgets/FormikInputFieldV2';
import { ContactFormValues } from '../constants';
import { ContactCategory } from '../../../../../constants';

const Container = styled.div`
  width: 350px;
`;

const InputGroup = styled.div`
  &:not(:last-child) {
    margin-bottom: 1rem;
  }
`;

const CheckboxLabel = styled(Typography.Label)`
  display: block;
`;

const CheckBoxOption: typeof Checkbox = styled(Checkbox)`
  padding-left: 1.5rem; // undo the negative margin from reactstrap
  display: block;
`;

const PRIMARY = ['primary'];

const useContactTypeOptions = () => {
  const {
    initialValues: { contactType },
  } = useFormikContext<ContactFormValues>();
  const isCurrentlyPrimary = contactType === 'primary';

  const primaryContact = useSelector(onboardingSelector.getPrimaryContact);

  return React.useMemo(() => {
    let primaryContactLabel;
    if (!primaryContact || isCurrentlyPrimary) {
      primaryContactLabel = (
        <span data-cy="primary-contact-label">Primary contact</span>
      );
    } else {
      primaryContactLabel = (
        <span data-cy="primary-contact-label">
          Primary contact (currently <b>{getFullName(primaryContact)}</b>
          {primaryContact.isMSP && ' - MSP'})
        </span>
      );
    }

    return [
      {
        value: 'primary',
        label: primaryContactLabel,
      },
      { value: 'secondary', label: 'Secondary contact' },
      { value: 'normal', label: 'Standard contact' },
    ];
  }, [primaryContact, isCurrentlyPrimary]);
};

export const SettingsInputs: React.FunctionComponent = () => {
  const primaryMSPContacts = useSelector(
    memoizedOnboardingSelectors.getPrimaryMSPContacts,
  );
  const isManagedRiskEnabled = useSelector(
    onboardingSelector.isManagedRiskEnabled,
  );
  const isLogSearchEnabled = useSelector(onboardingSelector.isLogSearchEnabled);
  const isManagedAwarenessEnabled = useSelector(
    onboardingSelector.isManagedAwarenessEnabled,
  );
  const hasIRRProduct = useSelector(onboardingSelector.hasIRRProduct);
  const onlyPurchasedIRRProducts = useSelector(
    onboardingSelector.onlyPurchasedIRRProducts,
  );

  const {
    values: { contactCategory },
  } = useFormikContext<ContactFormValues>();

  const contactTypeOptions = useContactTypeOptions();

  if (contactCategory === ContactCategory.SC) {
    return <Container>No settings available for shipping contacts.</Container>;
  }

  return (
    <Container>
      {contactCategory === ContactCategory.USER && (
        <InputGroup>
          <RadioButtons
            name="contactType"
            label="Designate contact as"
            options={contactTypeOptions}
            disabledOptions={primaryMSPContacts.length ? PRIMARY : undefined}
          />
        </InputGroup>
      )}

      <InputGroup>
        <CheckboxLabel>Add contact to</CheckboxLabel>
        <CheckBoxOption
          name="isBasicEscalationContact"
          label="Basic Escalation Contacts"
        />
        <CheckBoxOption
          name="isExecutiveSponsor"
          label="Executive Sponsor Contacts"
        />
      </InputGroup>

      <InputGroup>
        <CheckboxLabel>Receive notifications for</CheckboxLabel>
        <CheckBoxOption name="isReportRecipient" label="Reports" />
      </InputGroup>

      {contactCategory === ContactCategory.USER && (
        <InputGroup data-cy="contact-access">
          <CheckboxLabel>Grant access to</CheckboxLabel>
          <CheckBoxOption name="isPortalUser" label="Arctic Wolf Portal" />
          {isLogSearchEnabled && (
            <CheckBoxOption name="isLogSearchUser" label="Data Explorer" />
          )}
          {isManagedRiskEnabled && (
            <CheckBoxOption
              name="isRiskUser"
              label="Managed Risk (MR) Dashboard"
            />
          )}
          {isManagedAwarenessEnabled && (
            <CheckBoxOption
              name="isManagedAwarenessUser"
              label="Managed Awareness"
            />
          )}
          {!onlyPurchasedIRRProducts && (
            <CheckBoxOption
              name="isActivateVlcUser"
              label="Activate Virtual Appliance Access"
            />
          )}
          {hasIRRProduct && (
            <CheckBoxOption
              name="isMyCyberDashboardUser"
              label="Cyber JumpStart"
            />
          )}
        </InputGroup>
      )}
    </Container>
  );
};
